<template>
	<div class="merchants-page">
		<div class="merchants-layout">
			<!-- Left Panel: Merchant List -->
			<div class="merchants-list-panel">
				<div class="list-header">
					<h2>Merchants</h2>
					<button class="action-btn primary" @click="createNewMerchant">+ New</button>
				</div>
				<div class="search-box">
					<input
						v-model="searchQuery"
						type="text"
						placeholder="Search merchants..."
						class="search-input"
					/>
				</div>
				<div class="merchants-list">
					<div
						v-if="filteredMerchants.length > 0"
						class="merchant-items"
					>
						<div
							v-for="merchant in filteredMerchants"
							:key="merchant.id"
							class="merchant-item"
							:class="{ active: selectedMerchant && selectedMerchant.id === merchant.id }"
							@click="selectMerchant(merchant)"
						>
							<img
								:src="getBrandLogoUrl(merchant.name)"
								class="merchant-logo"
								@error="$event.target.src = require('@/assets/round3.png')"
							/>
							{{ merchant.name }}
						</div>
					</div>
					<div v-else class="empty-state">
						No merchants found
					</div>
				</div>
			</div>

			<!-- Right Panel: Merchant Details -->
			<div class="merchant-details-panel">
				<div v-if="selectedMerchant" class="details-content">
					<!-- Merchant ID & Logo -->
					<div class="detail-header">
						<img
							v-if="editingData.name"
							:src="getBrandLogoUrl(editingData.name)"
							class="detail-logo"
							@error="$event.target.src = require('@/assets/round3.png')"
						/>
						{{ selectedMerchant.id || 'New Merchant' }}
					</div>

					<!-- Top Actions -->
					<div class="top-actions">
						<button class="action-btn primary" @click="saveMerchant">
							{{ selectedMerchant.id ? 'Save Changes' : 'Create Merchant' }}
						</button>
		<button v-if="selectedMerchant.id" class="action-btn secondary" @click="duplicateMerchant">Duplicate</button>
						<button v-if="selectedMerchant.id" class="action-btn danger" @click="showDeleteConfirm = true">Delete</button>
						<button v-if="selectedMerchant.id" class="action-btn secondary" @click="reloadMerchant">Reload</button>
					</div>

					<!-- Name & Regex -->
					<div class="form-row">
						<div class="form-group">
							<label>Name:</label>
							<input v-model="editingData.name" type="text" class="form-input" />
						</div>
						<div class="form-group">
							<label>Regex:</label>
							<input v-model="editingData.regex" type="text" class="form-input" />
						</div>
					</div>

					<!-- Regex Exclude -->
					<div class="form-row">
						<div class="form-group">
							<label>
								Regex Exclude:
								<span class="tooltip-icon" title="If the merchant matches the regex above but also matches this regex, it will be excluded from well known merchant matching.">?</span>
							</label>
							<input v-model="editingData.regexExclude" type="text" class="form-input" />
						</div>
					</div>

					<!-- Limits -->
					<div class="form-row">
						<div class="form-group">
							<label>Lower Limit:</label>
							<input v-model="editingData.lowerLimit" type="text" class="form-input" />
						</div>
						<div class="form-group">
							<label>Upper Limit:</label>
							<input v-model="editingData.upperLimit" type="text" class="form-input" />
						</div>
					</div>

					<!-- Checkboxes -->
					<div class="checkbox-row">
						<div class="form-group checkbox">
							<label>
								<input v-model="editingData.utilityProvider" type="checkbox" class="form-checkbox" />
								Utility
							</label>
						</div>
						<div class="form-group checkbox">
							<label>
								<input v-model="editingData.switchDealAvailable" type="checkbox" class="form-checkbox" />
								Switch Deal
							</label>
						</div>
						<div class="form-group checkbox">
							<label>
								<input v-model="editingData.notRecurring" type="checkbox" class="form-checkbox" />
								Not Recurring
							</label>
						</div>
					</div>

					<!-- Voucher Info (read-only) -->
					<div v-if="discountString" class="form-group">
						<label>Voucher Available:</label>
						<div class="voucher-display">{{ discountString }}</div>
					</div>

					<!-- Classification -->
					<div class="form-group">
						<label>Classification:</label>
						<div class="classification-row">
							<input
								:value="editingData.transactionClassification ? editingData.transactionClassification.fullName : ''"
								type="text"
								class="form-input"
								readonly
							/>
							<button class="action-btn secondary" @click="showClassifications = true">Classify</button>
						</div>
					</div>
				</div>
				<div v-else class="empty-state">
					Select a merchant to view details
				</div>
			</div>
		</div>

		<!-- Classifications Drawer -->
		<div v-if="showClassifications" class="drawer-overlay" @click="showClassifications = false">
			<div class="drawer" @click.stop>
				<div class="drawer-header">
					<h3>Classifications</h3>
					<button class="close-btn" @click="showClassifications = false">&#10005;</button>
				</div>
				<div class="drawer-content">
					<div v-for="parent in transactionClassifications" :key="parent.id">
						<button class="cat-btn parent-cat" @click="selectClassification(parent)">
							{{ parent.mgClassification.mgClassification }}
						</button>
						<button
							v-for="child in parent.children"
							:key="child.mgClassification.id"
							class="cat-btn child-cat"
							@click="selectClassification(child)"
						>
							{{ child.mgClassification.mgSubClassification }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Delete Confirmation -->
		<div v-if="showDeleteConfirm" class="modal-overlay" @click="showDeleteConfirm = false">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Delete Merchant</h3>
					<button class="close-btn" @click="showDeleteConfirm = false">&#10005;</button>
				</div>
				<div class="modal-body">
					<p>Are you sure you want to delete <strong>{{ selectedMerchant.name }}</strong>?</p>
				</div>
				<div class="modal-footer">
					<button class="action-btn danger" @click="deleteMerchant">Delete</button>
					<button class="action-btn secondary" @click="showDeleteConfirm = false">Cancel</button>
				</div>
			</div>
		</div>

		<!-- Success Toast -->
		<v-snackbar v-model="showSuccessToast" :timeout="3000" location="bottom" color="success">
			{{ successMessage }}
			<template #actions>
				<v-btn icon="mdi-close" size="small" @click="showSuccessToast = false"></v-btn>
			</template>
		</v-snackbar>

		<!-- Error Toast -->
		<v-snackbar v-model="showErrorToast" :timeout="4000" location="bottom" color="error">
			{{ errorMessage }}
			<template #actions>
				<v-btn icon="mdi-close" size="small" @click="showErrorToast = false"></v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';

export default {
	name: 'MerchantsPage',
	mixins: [AdminServices],
	data() {
		return {
			merchants: [],
			selectedMerchant: null,
			editingData: {},
			searchQuery: '',
			transactionClassifications: [],
			showClassifications: false,
			showDeleteConfirm: false,
			showSuccessToast: false,
			successMessage: '',
			showErrorToast: false,
			errorMessage: ''
		};
	},
	computed: {
		filteredMerchants() {
			if (!this.searchQuery.trim()) {
				return this.merchants;
			}
			const query = this.searchQuery.toLowerCase();
			return this.merchants.filter(m => m.name.toLowerCase().includes(query));
		},
		discountString() {
			const discount = this.selectedMerchant ? this.selectedMerchant.discount : null;
			if (!discount) return '';
			let type = '';
			if (discount.online && discount.inStore) {
				type = 'Online & In Store';
			} else if (discount.online) {
				type = 'Online';
			} else if (discount.inStore) {
				type = 'In Store';
			}
			return discount.discountPercentage + '% ' + type;
		}
	},
	methods: {
		getBrandLogoUrl(name) {
			if (!name) return require('@/assets/round3.png');
			return 'https://api.sysodev.link/BrandLogos/' + this._cleanBrandName(name).replace(/[ /]/g, '+');
		},
		showToast(message) {
			this.successMessage = message;
			this.showSuccessToast = true;
		},
		showError(message) {
			this.errorMessage = message;
			this.showErrorToast = true;
		},
		loadMerchants() {
			this._getMerchants((result) => {
				this.merchants = result;
			});
		},
		selectMerchant(merchant) {
			this.selectedMerchant = merchant;
			this.editingData = {
				name: merchant.name,
				regex: merchant.regex,
				regexExclude: merchant.regexExclude || '',
				lowerLimit: merchant.lowerLimit,
				upperLimit: merchant.upperLimit,
				utilityProvider: merchant.utilityProvider || false,
				switchDealAvailable: merchant.switchDealAvailable || false,
				notRecurring: merchant.notRecurring || false,
				transactionClassification: merchant.transactionClassification || null,
				setTransactionClassificationID: merchant.transactionClassification ? merchant.transactionClassification.id : null
			};
		},
		duplicateMerchant() {
			this.selectedMerchant = {
				id: null,
				name: this.editingData.name + ' (copy)',
				regex: this.editingData.regex,
				regexExclude: this.editingData.regexExclude,
				lowerLimit: this.editingData.lowerLimit,
				upperLimit: this.editingData.upperLimit,
				utilityProvider: this.editingData.utilityProvider,
				switchDealAvailable: this.editingData.switchDealAvailable,
				notRecurring: this.editingData.notRecurring
			};
			this.editingData = {
				...this.editingData,
				name: this.editingData.name + ' (copy)',
				setTransactionClassificationID: this.editingData.setTransactionClassificationID
			};
		},
		createNewMerchant() {
			this.selectedMerchant = {
				id: null,
				name: '',
				regex: '',
				regexExclude: '',
				lowerLimit: null,
				upperLimit: null,
				utilityProvider: false,
				switchDealAvailable: false,
				notRecurring: false
			};
			this.editingData = {
				name: '',
				regex: '',
				regexExclude: '',
				lowerLimit: null,
				upperLimit: null,
				utilityProvider: false,
				switchDealAvailable: false,
				notRecurring: false,
				transactionClassification: null,
				setTransactionClassificationID: null
			};
		},
		saveMerchant() {
			const merchantData = {
				...this.selectedMerchant,
				name: this.editingData.name,
				regex: this.editingData.regex,
				regexExclude: this.editingData.regexExclude,
				lowerLimit: this.editingData.lowerLimit,
				upperLimit: this.editingData.upperLimit,
				utilityProvider: this.editingData.utilityProvider || false,
				switchDealAvailable: this.editingData.switchDealAvailable || false,
				notRecurring: this.editingData.notRecurring || false,
				setTransactionClassificationID: this.editingData.setTransactionClassificationID
			};

			this._saveMerchant(merchantData, (saved) => {
				this.selectedMerchant = saved;
				this.selectMerchant(saved);
				this.loadMerchants();
				this.showToast('Merchant saved successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		deleteMerchant() {
			this._deleteMerchant(this.selectedMerchant.id, () => {
				this.showDeleteConfirm = false;
				this.selectedMerchant = null;
				this.loadMerchants();
				this.showToast('Merchant deleted successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		reloadMerchant() {
			this.loadMerchants();
			this._getTransactionClassifications((result) => {
				this.transactionClassifications = result;
			});
			this.showToast('Reloaded');
		},
		selectClassification(item) {
			this.editingData.transactionClassification = item.mgClassification;
			this.editingData.setTransactionClassificationID = item.mgClassification.id;
			this.showClassifications = false;
		}
	},
	mounted() {
		this.loadMerchants();
		this._getTransactionClassifications((result) => {
			this.transactionClassifications = result;
		});
	}
};
</script>

<style scoped>
.merchants-page {
	min-height: 100%;
	padding: 20px;
}

.merchants-layout {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
	height: calc(100vh - 40px);
}

/* Left Panel */
.merchants-list-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	display: flex;
	flex-direction: column;
	box-shadow: var(--box-shadow);
	overflow-y: auto;
}

.list-header {
	margin-bottom: 15px;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.list-header h2 {
	margin: 0;
	font-size: 16px;
	color: var(--admin-text-primary);
}

.merchants-list {
	flex: 1;
	overflow-y: auto;
}

.merchant-items {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.merchant-item {
	padding: 12px;
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
	display: flex;
	align-items: center;
	gap: 10px;
}

.merchant-logo {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
}

.merchant-item:hover {
	background-color: var(--admin-hover-bg);
}

.merchant-item.active {
	background-color: var(--admin-active-bg);
	border-left-color: var(--background-color-primary);
}

/* Right Panel */
.merchant-details-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 30px;
	overflow-y: auto;
	box-shadow: var(--box-shadow);
}

.details-content {
	max-width: 900px;
}

.detail-header {
	font-size: 13px;
	color: var(--admin-text-secondary);
	margin-bottom: 20px;
	padding: 10px;
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-s);
	font-family: monospace;
	display: flex;
	align-items: center;
	gap: 12px;
}

.detail-logo {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
}

/* Form */
.form-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 15px;
	margin-bottom: 20px;
}

.form-group {
	margin-bottom: 20px;
}

.form-group.checkbox {
	margin-bottom: 0;
}

.form-group.checkbox label {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
}

.checkbox-row {
	display: flex;
	gap: 30px;
	margin-bottom: 20px;
}

.tooltip-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background-color: var(--admin-text-secondary);
	color: var(--admin-bg-secondary);
	font-size: 11px;
	margin-left: 6px;
}

.voucher-display {
	font-size: 13px;
	color: var(--admin-text-primary);
	padding: 10px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
}

.classification-row {
	display: flex;
	gap: 10px;
	align-items: center;
}

.classification-row .form-input {
	flex: 1;
}

/* Top Actions */
.top-actions {
	display: flex;
	gap: 10px;
	margin-bottom: 30px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--admin-border);
}

/* Drawer */
.drawer-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: flex-end;
	z-index: 1000;
	animation: fadeIn 0.2s ease;
}

.drawer {
	background-color: var(--admin-bg-secondary);
	width: 400px;
	height: 80vh;
	display: flex;
	flex-direction: column;
	box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
	animation: slideIn 0.3s ease;
	margin-top: 100px;
}

@keyframes slideIn {
	from { transform: translateX(100%); }
	to { transform: translateX(0); }
}

@keyframes fadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}

.drawer-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.drawer-header h3 {
	margin: 0;
	font-size: 18px;
	color: var(--admin-text-primary);
}

.drawer-content {
	flex: 1;
	padding: 20px;
	overflow-y: auto;
}

.cat-btn {
	display: block;
	width: 100%;
	text-align: left;
	border: none;
	padding: 10px 12px;
	margin-bottom: 4px;
	border-radius: var(--border-radius-s);
	color: white;
	font-size: 13px;
	transition: opacity 0.2s ease;
}

.cat-btn:hover {
	opacity: 0.85;
}

.parent-cat {
	background-color: var(--background-color-alt2);
}

.child-cat {
	background-color: var(--background-color-primary);
	margin-left: 16px;
	width: calc(100% - 16px);
	font-size: 12px;
}

/* Responsive */
@media (max-width: 1024px) {
	.merchants-layout {
		grid-template-columns: 250px 1fr;
	}
}

@media (max-width: 480px) {
	.merchants-layout {
		grid-template-columns: 1fr;
		gap: 15px;
	}

	.merchants-list-panel {
		max-height: 400px;
	}

	.form-row {
		grid-template-columns: 1fr;
	}

	.merchants-page {
		padding: 10px;
	}

	.checkbox-row {
		flex-direction: column;
		gap: 10px;
	}

	.drawer {
		width: 100%;
	}
}

/* Snackbar positioning */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}
</style>
